import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import { formatChallengeRating } from '../utils/challengeRatings';

const ABILITIES = [['str', 'STR'], ['dex', 'DEX'], ['con', 'CON'], ['int', 'INT'], ['wis', 'WIS'], ['cha', 'CHA']];
const GROUPS = [
  ['specialAbilities', 'Traits'], ['actions', 'Actions'], ['bonusActions', 'Bonus Actions'],
  ['reactions', 'Reactions'], ['legendaryActions', 'Legendary Actions'],
  ['lairActions', 'Lair Actions'], ['regionalEffects', 'Regional Effects'],
];
const text = (value) => Array.isArray(value) ? value.join(', ') : value || '—';
const pairs = (value) => value && typeof value === 'object'
  ? Object.entries(value).map(([key, item]) => `${key}: ${item}`).join(', ') || '—'
  : text(value);

export default function MonsterStatBlock({ monster }) {
  if (!monster) return null;
  const armorClass = (monster.armorClass || []).map((entry) => entry.value ?? entry).join(', ') || '—';
  const speed = pairs(monster.speed);
  return <Card className="bg-dark border-secondary text-start text-light" data-testid="monster-stat-block">
    <Card.Body>
      <div className="d-flex gap-3 align-items-start">
        {monster.tokenImage?.secureUrl && <img src={monster.tokenImage.secureUrl} alt={`${monster.name} token`} width="80" height="80" style={{ objectFit: 'contain' }} />}
        <div><Card.Title>{monster.name}</Card.Title><Card.Subtitle className="text-white-50">
          {[monster.size, monster.type, monster.subtype].filter(Boolean).join(' ')} • {monster.alignment || 'unaligned'}
        </Card.Subtitle><Badge bg="info" text="dark" className="mt-2">Custom</Badge></div>
      </div>
      <hr />
      <p className="mb-1"><strong>Armor Class</strong> {armorClass}</p>
      <p className="mb-1"><strong>Hit Points</strong> {monster.hitPoints ?? '—'} ({monster.hitDice || '—'})</p>
      <p><strong>Speed</strong> {speed}</p>
      <div className="d-flex flex-wrap gap-2 mb-3">{ABILITIES.map(([key, label]) => <Badge bg="secondary" key={key}>{label} {monster.abilityScores?.[key] ?? '—'}</Badge>)}</div>
      <p className="mb-1"><strong>Saving Throws</strong> {pairs(monster.savingThrows)}</p>
      <p className="mb-1"><strong>Skills</strong> {pairs(monster.skills)}</p>
      <p className="mb-1"><strong>Damage Vulnerabilities</strong> {text(monster.damageVulnerabilities)}</p>
      <p className="mb-1"><strong>Damage Resistances</strong> {text(monster.damageResistances)}</p>
      <p className="mb-1"><strong>Damage Immunities</strong> {text(monster.damageImmunities)}</p>
      <p className="mb-1"><strong>Condition Immunities</strong> {text(monster.conditionImmunities)}</p>
      <p className="mb-1"><strong>Senses</strong> {pairs(monster.senses)}</p>
      <p className="mb-1"><strong>Languages</strong> {text(monster.languages)}</p>
      <p><strong>Challenge</strong> {formatChallengeRating(monster.challengeRating)} ({Number(monster.xp || 0).toLocaleString()} XP) • <strong>Proficiency Bonus</strong> +{monster.proficiencyBonus ?? '—'}</p>
      {GROUPS.map(([key, heading]) => Array.isArray(monster[key]) && monster[key].length > 0 && <section key={key} className="mt-3"><h6>{heading}</h6>{monster[key].map((entry, index) => <p key={`${key}-${index}`}><strong>{entry.name || heading}</strong>{entry.desc ? `. ${entry.desc}` : ''}</p>)}</section>)}
    </Card.Body>
  </Card>;
}
